import { useApiClient } from '../../hooks/useApiClient';
import { ApiResponse } from '../../types/ApiResponse.type';
import { PresignedPost } from '../../types/PresignedUpload.type';
import { GetResourcesQueryParams } from '../../types/QueryParams.type';
import { Resource } from '../../types/Resources/Resource.type';
import { ResourceCreate } from '../../types/Resources/ResourceCreate.type';
import { ResourceReview } from '../../types/Resources/ResourceReview.type';
import { ResourceUpdate } from '../../types/Resources/ResourceUpdate.type';
import { buildQueryString } from '../../utils/buildQueryString';
import { uploadToPresignedPost } from '../../utils/uploadToPresignedPost';

export const useResourceApi = () => {
  const apiClient = useApiClient();

  const getPresignedUrl = (file: File): Promise<PresignedPost> => {
    return apiClient('/resources/uploads/presign', {
      method: 'POST',
      body: JSON.stringify({ file_name: file.name, content_type: file.type }),
    });
  };

  return {
    getResources: (params: GetResourcesQueryParams): Promise<ApiResponse<Resource[]>> => {
      const queryString = buildQueryString(params);

      return apiClient(`/resources/${queryString}`);
    },

    getResourceById: (resourceId: number): Promise<Resource> => {
      return apiClient(`/resources/${resourceId}`);
    },

    createResource: async (resourceData: ResourceCreate): Promise<Resource> => {
      let filePath: string | undefined;
      if (resourceData.type_id === -1 && resourceData.file) {
        const presignedPost = await getPresignedUrl(resourceData.file);
        filePath = await uploadToPresignedPost(presignedPost, resourceData.file);
      }

      return apiClient('/resources/', {
        method: 'POST',
        body: JSON.stringify({
          name: resourceData.name,
          description: resourceData.description,
          type_id: resourceData.type_id,
          file_path: filePath,
          link: resourceData.type_id === -2 ? resourceData.link : undefined,
          resource_page_only: resourceData.resource_page_only,
          scope_id: resourceData.scope_id,
          statutes: resourceData.statutes,
        }),
      });
    },

    reviewResource: (resourceId: number, reviewData: Partial<ResourceReview>): Promise<Resource> => {
      return apiClient(`/resources/${resourceId}/review`, {
        method: 'POST',
        body: JSON.stringify(reviewData),
      });
    },

    updateResource: async (resourceId: number, updateData: Partial<ResourceUpdate>): Promise<Resource> => {
      let filePath: string | undefined;
      if (updateData.file) {
        const presignedPost = await getPresignedUrl(updateData.file);
        filePath = await uploadToPresignedPost(presignedPost, updateData.file);
      }

      return apiClient(`/resources/${resourceId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: updateData.name,
          description: updateData.description,
          link: updateData.link,
          file_path: filePath,
          resource_page_only: updateData.resource_page_only,
          scope_id: updateData.scope_id,
          statutes: updateData.statutes,
        }),
      });
    },

    toggleFavorite: (resourceId: number): Promise<{ is_favorite: boolean }> => {
      return apiClient(`/resources/${resourceId}/favorite`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    deleteResource: (resourceId: number): Promise<void> => {
      return apiClient(`/resources/${resourceId}`, {
        method: 'DELETE',
      });
    },
  };
};
